import { useEffect, useLayoutEffect } from "react";
import { useStableCallback } from "./useStableCallback";

const isEventFromFormElement = (event: Event) => {
	// Allow form elements to handle their own key events
	const tagName = (event.target as unknown as HTMLElement)?.tagName?.toUpperCase();
	if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") return true;
	return false;
};

function getKeyCombination(event: KeyboardEvent): string {
	const keys: string[] = [];
	if (event.ctrlKey) keys.push("Ctrl");
	if (event.shiftKey) keys.push("Shift");
	if (event.altKey) keys.push("Alt");
	keys.push(event.key);
	return keys.join(" + ");
}

/**
 * Remember stack of active key trap elements
 * Only events from the last added element are handled
 * Remark:
 * - Previously KeyTrap prevented key pressed from bubbling up.
 * This was not ideal as it also blocked all browser default key presses, F11, Ctrl+T (new tab), ...
 * - If a 3rd party component is listening for key events, this key trap will not have effect on it
 */
const keyTraps: HTMLElement[] = [];

function useKeyEvent(
	eventName: "keydown" | "keyup",
	/** No stable reference needed */
	callback: (event: KeyboardEvent) => void,
	{
		enabled = true,
		name = "",
		logger,
		enableOnFormTags = false,
		capture = false,
		ignoreTrap = false,
		ref,
	}: {
		enabled?: boolean;

		/** For debugging purposes */
		name?: string;
		logger?: eXpress.ILogger;

		/**
		 * By default we don't add or replace key events on form elements (input, textarea, select, ...).
		 * Example: Ctrl+Z or Arrow key on input element
		 * Set to true to allow event on form elements
		 */
		enableOnFormTags?: boolean;

		/**
		 * Handle event in the capture vs bubbling phase
		 */
		capture?: boolean;

		/**
		 * Ignore key traps (default = false)
		 * Normally, if a popup has a key trap active, other scopes or global scope won't trigger key events.
		 * ignoreTrap can be usefull when preventing default browser keys. Example: Ctrl+S
		 */
		ignoreTrap?: boolean;

		/** subscribe on this element */
		ref?: React.RefObject<HTMLElement | null | undefined> | null | undefined;
	} = {},
) {
	const stableCallback = useStableCallback((event: KeyboardEvent) => {
		// If KeyTrap is enabled, only allow keys from the last trap scope
		const trap = keyTraps.at(-1);
		if (!ignoreTrap && trap && enabled && trap !== ref?.current) {
			logger?.log(
				`Key combination '${getKeyCombination(event)}' from '${
					name || "?"
				}' cancelled because scoped to '${ref?.current?.className}' and trapped in '${
					trap.className
				}'`,
			);

			return;
		}

		// Allow form elements to handle their own key events
		if (!enableOnFormTags && isEventFromFormElement(event)) {
			logger?.log(
				`Key combination '${getKeyCombination(event)}' from '${
					name || "?"
				}' cancelled because triggered from a form element ${(
					event.target as unknown as HTMLElement
				)?.tagName?.toUpperCase()}`,
			);
			return;
		}

		logger?.log(
			`Handler for key combination '${getKeyCombination(event)}' from '${
				name || "?"
			}' is called`,
		);
		callback(event);
	}) as (event: Event) => void;

	// Use effect runs when it is mounted so element from ref normally available
	useEffect(() => {
		if (!enabled || ref === null) return undefined;

		const el = ref === undefined ? document : ref.current;
		if (!el) return undefined;
		el.addEventListener(eventName, stableCallback, { capture });
		return () => {
			el.removeEventListener(eventName, stableCallback, { capture });
		};
	}, [ref, enabled, eventName, stableCallback, capture]);
}

/**
 * By default callback is not called when key event comes from a form element, use `enableOnFormTags` to allow it.
 */
export function useKeyDown(
	/** No stable reference needed */
	callback: (event: KeyboardEvent) => void,
	options: Parameters<typeof useKeyEvent>[2] = {},
) {
	useKeyEvent("keydown", callback, options);
}

/**
 * By default callback is not called when key event comes from a form element, use `enableOnFormTags` to allow it.
 */
export function useKeyUp(
	/** No stable reference needed */
	callback: (event: KeyboardEvent) => void,
	options: Parameters<typeof useKeyEvent>[2] = {},
) {
	useKeyEvent("keyup", callback, options);
}

/**
 * Only allow keys triggered from this ref element
 * (Only works in combination with useKeyDown)
 * We keep a stack of key traps, only the last one accepts keys
 */
export function useKeyTrap(
	ref: React.RefObject<HTMLElement | null | undefined> | null | undefined,
	{ enabled = true } = {},
) {
	// Remember key traps
	// Only keys of the last scope are handled
	useLayoutEffect(() => {
		if (!enabled) return undefined;

		const el = ref?.current;
		if (!el) return undefined;

		// Add key trap
		keyTraps.push(el);

		// Remove key trap
		return () => {
			const index = keyTraps.lastIndexOf(el);
			if (index >= 0) keyTraps.splice(index, 1);
		};
	}, [ref, enabled]);
}
